import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  RiArrowRightLine,
  RiAwardLine,
  RiTeamLine,
  RiGlobalLine,
  RiUserStarLine,
  RiHeartLine,
  RiLightbulbLine,
  RiRocketLine,
  RiStarFill,
  RiCheckLine,
  RiBookOpenLine,
  RiComputerLine,
  RiUserVoiceLine,
  RiCommunityLine,
  RiChatQuoteLine,
  RiDiscussLine,
  RiEarthLine,
  RiCrosshairLine,
  RiEyeLine,
  RiArrowUpLine,
} from 'react-icons/ri';

/* ─── THEME COLORS (Teal & Gold Palette) ───────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
};

/* ─── RESPONSIVE CSS ──────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(40px,7vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(1.8rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.85rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:14px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0}
  .t-st{background:${T.white};padding:20px 28px;display:flex;flex-direction:column;gap:4px;min-width:100px}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:11px;color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.08em}

  .t-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:28px;box-shadow:${T.shadow};transition:all 0.3s ease}
  .t-card:hover{transform:translateY(-6px);box-shadow:${T.shadowLg}}
  
  .t-cta-w{padding:0 clamp(16px,4vw,64px) 48px;background:${T.offWhite}}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:20px;padding:clamp(24px,4vw,52px) clamp(24px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:11px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.2rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .t-cta-p{font-size:14px;color:${T.textMuted};margin-top:8px;line-height:1.6}
  .t-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s;white-space:nowrap}
  .t-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}
  
  .t-footer{border-top:1px solid ${T.border};background:${T.white};padding:28px clamp(16px,4vw,64px);margin-top:40px}
  .t-fi{max-width:1320px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px}
  .t-f-copy{font-size:12px;color:${T.textMuted};letter-spacing:.01em}
  
  @keyframes spin{to{transform:rotate(360deg)}}
  
  .team-img-container {
    width: 100%;
    height: 280px;
    overflow: hidden;
    position: relative;
  }
  .team-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    transition: transform 0.5s ease;
  }
  .team-img:hover {
    transform: scale(1.05);
  }

  /* ─── RESPONSIVE OVERRIDES ─── */
  @media (max-width: 1024px) {
    .t-hero-i { flex-direction: column; align-items: center; text-align: center; }
    .t-hero-p { max-width: 100%; }
    .t-stats { align-self: center; }
  }
  @media (max-width: 768px) {
    .t-hero { padding: 32px 16px 24px; }
    .t-stats { flex-direction: column; width: 100%; border-radius: 12px; }
    .t-st { padding: 16px; align-items: center; }
    .t-st-n { font-size: 1.5rem; }
    .t-cta { flex-direction: column; text-align: center; padding: 28px 20px; }
    .t-cta-r { width: 100%; }
    .t-cta-btn { width: 100%; justify-content: center; white-space: normal; }
    .t-fi { flex-direction: column; text-align: center; gap: 12px; }
    .t-card { padding: 20px; }
    .about-grid { grid-template-columns: 1fr !important; }
    .about-grid-2 { grid-template-columns: 1fr !important; }
  }
  @media (max-width: 480px) {
    .t-hero { padding: 24px 12px 20px; }
    .t-hero-h { font-size: 1.6rem; }
    .t-cta-h { font-size: 1.2rem; }
    .t-card { padding: 16px; }
    .t-st-n { font-size: 1.3rem; }
  }
`;

// Stats data
const aboutStats = [
  { icon: RiTeamLine, value: '10+', label: 'Academic Conferences', description: 'Successfully Organized' },
  { icon: RiGlobalLine, value: '30+', label: 'Countries', description: 'Across the Globe' },
  { icon: RiBookOpenLine, value: '15+', label: 'Subjects', description: 'Academic Disciplines' },
  { icon: RiUserVoiceLine, value: '100+', label: 'Participants', description: 'Per Conference' },
  { icon: RiCommunityLine, value: '10+', label: 'Exhibitors', description: 'Industry Partners' },
  { icon: RiUserStarLine, value: '200+', label: 'Delegates', description: 'Per Conference' },
];

// Mission & Vision points
const missionPoints = [
  "Facilitate global collaboration among researchers and academics",
  "Provide high-quality, accessible conference experiences",
  "Foster interdisciplinary dialogue and knowledge exchange",
  "Support early-career researchers and emerging scholars"
];

const visionPoints = [
  "To become the world's most trusted academic conference organizer",
  "Creating lasting impact through meaningful scholarly connections",
  "Bridging gaps between disciplines and continents",
  "Empowering researchers to share their work with global audiences"
];

// Values
const values = [
  { icon: RiLightbulbLine, title: 'Academic Excellence', description: 'Curating cutting-edge research and fostering scholarly dialogue that pushes boundaries across disciplines.' },
  { icon: RiComputerLine, title: 'Tech Innovation', description: 'Virtual attendance, interactive sessions, and seamless digital networking for global participation.' },
  { icon: RiTeamLine, title: 'Cross-Disciplinary', description: 'Bringing together diverse fields to create a rich tapestry of ideas and collaborative breakthroughs.' },
  { icon: RiHeartLine, title: 'Passionate Service', description: 'Meticulous planning so you focus on content, connections, and meaningful intellectual exchange.' },
  { icon: RiEarthLine, title: 'Global Reach', description: 'Connecting scholars from over 60 countries through our extensive network and partnerships.' },
  { icon: RiDiscussLine, title: 'Open Dialogue', description: 'Creating inclusive spaces for debate, discussion, and the free exchange of ideas.' },
];

// Team members with reliable Unsplash images (using different images for variety)
const teamMembers = [
  { name: 'Dr. Sarah Chen', role: 'Executive Director', field: 'Higher Education Leadership', bio: '20+ years in academic conference management', image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=500&h=500&fit=crop', gender: 'female' },
  { name: 'Prof. Michael Okonkwo', role: 'Academic Chair', field: 'Research & Innovation', bio: 'Former Dean of Research at Oxford', image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=500&h=500&fit=crop', gender: 'male' },
  { name: 'Dr. Priya Sharma', role: 'Program Director', field: 'Global Partnerships', bio: 'Expert in international academic relations', image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=500&h=500&fit=crop', gender: 'female' },
  { name: 'Prof. James Wilson', role: 'Advisory Board', field: 'Digital Learning', bio: 'Pioneer in educational technology', image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=500&h=500&fit=crop', gender: 'male' },
  { name: 'Dr. Maria Santos', role: 'Operations Director', field: 'Event Logistics', bio: '15+ years managing global conferences', image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=500&h=500&fit=crop', gender: 'female' },
  { name: 'Prof. David Kim', role: 'Research Chair', field: 'Scientific Publications', bio: 'Editor of 3 leading academic journals', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&h=500&fit=crop', gender: 'male' },
];

// Testimonials
const testimonials = [
  { name: 'Dr. Emily Watson', university: 'Cambridge University', text: 'Meetings21 transformed our annual symposium. Their attention to detail and academic rigor is unmatched.', rating: 5, role: 'Professor of Physics' },
  { name: 'Prof. David Kim', university: 'Seoul National University', text: 'The networking opportunities and quality of presentations exceeded all expectations. Highly recommended!', rating: 5, role: 'Research Director' },
  { name: 'Dr. Maria Garcia', university: 'University of Toronto', text: 'A truly global platform that brings together the brightest minds. The virtual attendance option was seamless.', rating: 5, role: 'Dean of Research' },
  { name: 'Prof. Ahmed Hassan', university: 'Cairo University', text: 'Meetings21 helped us establish international collaborations that led to groundbreaking research.', rating: 5, role: 'Department Chair' },
];

// Stat Card Component
function AboutStatCard({ stat, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const [count, setCount] = useState(0);
  const rawValue = parseInt(stat.value) || 0;

  useEffect(() => {
    if (inView) {
      let start = 0;
      const duration = 1500;
      const increment = rawValue / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= rawValue) {
          setCount(rawValue);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [inView, rawValue]);

  const displayValue = stat.value.includes('%') ? `${count}%` : `${count}+`;
  const gradients = [T.gradPrimary, T.gradGold, T.gradTealDark, T.gradPrimary, T.gradGold, T.gradTealDark];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="t-card"
      style={{ textAlign: 'center' }}
    >
      <div style={{
        width: 60,
        height: 60,
        borderRadius: '50%',
        background: gradients[index % gradients.length],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 12px',
      }}>
        <stat.icon size={28} color={T.white} />
      </div>
      <div style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 36,
        fontWeight: 900,
        background: gradients[index % gradients.length],
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        marginBottom: 6,
      }}>{displayValue}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{stat.label}</div>
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{stat.description}</div>
    </motion.div>
  );
}

// Value Card Component
function ValueCard({ value, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const gradients = [T.gradPrimary, T.gradGold, T.gradTealDark, T.gradPrimary, T.gradGold, T.gradTealDark];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="t-card"
      style={{ textAlign: 'center' }}
    >
      <div style={{
        width: 60,
        height: 60,
        borderRadius: '50%',
        background: gradients[index % gradients.length],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
      }}>
        <value.icon size={28} color={T.white} />
      </div>
      <h3 style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 20,
        fontWeight: 800,
        color: T.text,
        marginBottom: 10,
      }}>{value.title}</h3>
      <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>{value.description}</p>
    </motion.div>
  );
}

// Team Card Component with fixed image display
function TeamCard({ member, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const gradients = [T.gradPrimary, T.gradGold, T.gradTealDark];

  const [imgError, setImgError] = useState(false);
  const fallbackImages = {
    female: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=500&h=500&fit=crop',
    male: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=500&h=500&fit=crop'
  };
  const imageSrc = imgError ? fallbackImages[member.gender] : member.image;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="t-card"
      style={{ textAlign: 'center', overflow: 'hidden', padding: 0 }}
    >
      <div style={{
        width: '100%',
        height: 280,
        overflow: 'hidden',
        background: gradients[index % gradients.length],
        position: 'relative',
      }}>
        <img 
          src={imageSrc} 
          alt={member.name}
          className="team-img"
          onError={() => setImgError(true)}
        />
      </div>
      <div style={{ padding: '20px' }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>{member.name}</h3>
        <p style={{ fontSize: 12, fontWeight: 600, color: T.accentGoldDark, marginBottom: 6 }}>{member.role}</p>
        <p style={{ fontSize: 12, color: T.textMuted }}>{member.field}</p>
        <p style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>{member.bio}</p>
      </div>
    </motion.div>
  );
}

// Testimonial Card Component
function TestimonialCard({ testimonial, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="t-card"
      style={{ position: 'relative' }}
    >
      <RiChatQuoteLine size={40} style={{ color: `${T.primary}20`, position: 'absolute', top: 20, right: 20 }} />
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {[...Array(testimonial.rating)].map((_, i) => (
          <RiStarFill key={i} size={16} color={T.accentGold} />
        ))}
      </div>
      <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.6, marginBottom: 20, fontStyle: 'italic' }}>
        "{testimonial.text}"
      </p>
      <div>
        <p style={{ fontWeight: 700, color: T.primaryDark }}>{testimonial.name}</p>
        <p style={{ fontSize: 12, color: T.textMuted }}>{testimonial.role}</p>
        <p style={{ fontSize: 11, color: T.accentGoldDark, marginTop: 4 }}>{testimonial.university}</p>
      </div>
    </motion.div>
  );
}

// Main About Component
export default function About() {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 500);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <style>{css}</style>
      
      <div style={{ minHeight: '100vh', background: T.offWhite, fontFamily: "'Inter', sans-serif" }}>
        
        {/* Hero Section */}
        <section className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl">
                <RiAwardLine size={13} />
                About Meetings21
              </div>
              <h1 className="t-hero-h">
                About <em>Meetings21</em>
              </h1>
              <p className="t-hero-p">
                We understand the importance of academic conferences in the 21st century to foster collaboration, 
                share knowledge, and advance innovation. We provide a platform for meaningful dialogue and 
                the exchange of cutting-edge research.
              </p>
            </div>
            <div className="t-stats">
              <div className="t-st">
                <span className="t-st-n">10+</span>
                <span className="t-st-l">Conferences</span>
              </div>
              <div className="t-st">
                <span className="t-st-n">30+</span>
                <span className="t-st-l">Countries</span>
              </div>
              <div className="t-st">
                <span className="t-st-n">200+</span>
                <span className="t-st-l">Delegates</span>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '24px 40px 0' }}>
          <div className="about-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24, marginBottom: 24 }}>
            {aboutStats.map((stat, index) => (
              <AboutStatCard key={stat.label} stat={stat} index={index} />
            ))}
          </div>
        </div>

        {/* Journey Message */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 40px 60px', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="t-card"
            style={{ maxWidth: 700, margin: '0 auto', borderTop: `4px solid ${T.accentGold}` }}
          >
            <RiRocketLine size={48} color={T.accentGold} style={{ marginBottom: 20 }} />
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 28,
              fontWeight: 800,
              color: T.text,
              marginBottom: 16,
            }}>
              Our Journey Has Just Begun
            </h2>
            <p style={{ fontSize: 16, color: T.textMid, lineHeight: 1.6 }}>
              Every great achievement starts with a single step. We're at the beginning of an exciting journey 
              to revolutionize academic conferences and create meaningful connections worldwide.
            </p>
          </motion.div>
        </div>

        {/* Mission & Vision Section */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 40px 60px' }}>
          <div className="about-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 32 }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="t-card"
              style={{ borderTop: `4px solid ${T.primary}` }}
            >
              <div style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: T.gradPrimary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 24,
              }}>
                <RiCrosshairLine size={28} color={T.white} />
              </div>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 28,
                fontWeight: 800,
                color: T.primaryDark,
                marginBottom: 20,
              }}>Our Mission</h2>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {missionPoints.map((point, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <RiCheckLine size={18} style={{ color: T.primary }} />
                    <span style={{ fontSize: 15, color: T.textMid }}>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="t-card"
              style={{ borderTop: `4px solid ${T.accentGold}` }}
            >
              <div style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: T.gradGold,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 24,
              }}>
                <RiEyeLine size={28} color={T.navyDeep} />
              </div>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 28,
                fontWeight: 800,
                color: T.accentGoldDark,
                marginBottom: 20,
              }}>Our Vision</h2>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {visionPoints.map((point, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <RiCheckLine size={18} style={{ color: T.accentGold }} />
                    <span style={{ fontSize: 15, color: T.textMid }}>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>

        {/* Our Values Section */}
        <div style={{ background: T.softTeal, padding: '60px 40px' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', textAlign: 'center' }}>
            <div className="t-hero-lbl" style={{ display: 'inline-flex', marginBottom: 16, background: T.white, color: T.accentGoldDark }}>Our Philosophy</div>
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(28px,4vw,38px)',
              fontWeight: 800,
              color: T.text,
              marginBottom: 12,
            }}>
              Our Core Values
            </h2>
            <p style={{ fontSize: 16, color: T.textMuted, marginBottom: 48, maxWidth: 650, margin: '0 auto 48px' }}>
              Passion for academic excellence drives everything we do
            </p>
            <div className="about-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 28,
            }}>
              {values.map((value, index) => (
                <ValueCard key={index} value={value} index={index} />
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="t-cta-w" style={{ padding: '0 40px 60px' }}>
          <motion.div
            className="t-cta"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
          >
            <div>
              <div className="t-cta-tag">Explore Events</div>
              <h2 className="t-cta-h">Discover Upcoming Academic Conferences</h2>
              <p className="t-cta-p">Join researchers from 60+ countries presenting at our global events.</p>
            </div>
            <div className="t-cta-r">
              <Link to="/events" className="t-cta-btn">
                Explore Events
                <RiArrowRightLine size={15} />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <div className="t-footer">
          <div className="t-fi">
            <span className="t-f-copy">© 2026 Meetings21. All rights reserved.</span>
            <span className="t-f-copy">Built with ❤️ for the global academic community.</span>
          </div>
        </div>

        {/* Back to Top Button */}
        {showTop && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              position: 'fixed',
              bottom: 26,
              right: 26,
              width: 42,
              height: 42,
              borderRadius: 10,
              background: T.gradGold,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: T.navyDeep,
              boxShadow: T.shadowGold,
              zIndex: 100,
            }}
          >
            <RiArrowUpLine size={18} />
          </button>
        )}
      </div>
    </>
  );
}